import { createMediaDeviceObserver } from '@livekit/components-core'
import {
  usePersistentUserChoices,
  usePreviewTracks,
} from '@livekit/components-react'
import { type LocalAudioTrack, type LocalVideoTrack } from 'livekit-client'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { DetailedPermissionState } from './av-test'

function mapErrorToDetailedState(e: Error): DetailedPermissionState {
  switch (e.name) {
    case 'NotAllowedError':
      return 'NotAllowed'
    case 'NotReadableError':
      return 'NotReadable'
    case 'OverconstrainedError':
      return 'Overconstrained'
    case 'SecurityError':
      return 'Security'
    default:
      return 'denied'
  }
}

// Subscribes to LiveKit's device-enumeration observable with
// `requestPermissions: false`. The default `useMediaDevices` hook passes
// `requestPermissions: true`, which makes the observer call
// `getUserMedia({[kind]: true})` per kind to unlock device labels — that's
// what produces the second permission prompt. We let `usePreviewTracks`
// be the single capture path; once it grants permission, the
// `devicechange` event re-fires the observer and labels appear without
// any extra prompt.
function useMediaDevicesWithNone({ kind }: { kind: MediaDeviceKind }) {
  const [devices, setDevices] = useState<MediaDeviceInfo[] | undefined>(
    undefined
  )

  useEffect(() => {
    const observer = createMediaDeviceObserver(
      kind,
      (e) => {
        if (e.name === 'NotFoundError') {
          setDevices([])
        }
      },
      false
    )
    const sub = observer.subscribe((next) => setDevices(next))
    return () => {
      sub.unsubscribe()
      setDevices(undefined)
    }
  }, [kind])

  return devices
}

export const useMediaDeviceListener = () => {
  const {
    userChoices: initialUserChoices,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices()

  const [audioDeviceId, setAudioDeviceId] = useState<string>(
    initialUserChoices.audioDeviceId
  )
  const [videoDeviceId, setVideoDeviceId] = useState<string>(
    initialUserChoices.videoDeviceId
  )

  const videoDevices = useMediaDevicesWithNone({ kind: 'videoinput' })
  const audioDevices = useMediaDevicesWithNone({ kind: 'audioinput' })

  useEffect(() => {
    if (!videoDevices) return
  }, [videoDevices])

  useLayoutEffect(() => {
    const listener = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter((d) => d.kind === 'videoinput')
      if (!videoDevices.some((device) => device.deviceId === videoDeviceId)) {
        const first = videoDevices[0]
        if (first) {
          setVideoDeviceId(first.deviceId)
        }
      }

      // handle default device - this is Chrome, but is also has an issue
      // when you plug out headphones, the device id does not change, but
      // the actual device does. So we make it switch to the real device
      // and then back to default to make sure that the browser handles
      // the change correctly
      if (audioDeviceId === 'default') {
        const defaultDevice = devices.find(
          (d) => d.kind === 'audioinput' && d.deviceId === 'default'
        )
        if (defaultDevice) {
          const realDevice = devices.find(
            (d) =>
              d.groupId === defaultDevice.groupId && d.deviceId !== 'default'
          )
          if (realDevice) {
            setAudioDeviceId(realDevice.deviceId)
            setTimeout(() => {
              setAudioDeviceId('default')
            }, 1)
          }
        }
      }

      const audioDevices = devices.filter((d) => d.kind === 'audioinput')
      if (!audioDevices.some((device) => device.deviceId === audioDeviceId)) {
        const first = audioDevices[0]
        if (first) {
          setAudioDeviceId(first.deviceId)
        }
      }
    }
    navigator?.mediaDevices?.addEventListener('devicechange', listener)

    return () => {
      navigator?.mediaDevices?.removeEventListener('devicechange', listener)
    }
  }, [videoDeviceId, audioDeviceId])

  const onActiveVideoDeviceChange = useCallback(
    (_: MediaDeviceKind, deviceId: string) => {
      saveVideoInputDeviceId(deviceId)
      setVideoDeviceId(deviceId)
    },
    [setVideoDeviceId, saveVideoInputDeviceId]
  )

  const onActiveAudioDeviceChange = useCallback(
    (_: MediaDeviceKind, deviceId: string) => {
      saveAudioInputDeviceId(deviceId)
      setAudioDeviceId(deviceId)
    },
    [setAudioDeviceId, saveAudioInputDeviceId]
  )

  // Surface the raw persisted/selected id. Two reasons not to validate it
  // against `videoDevices` here:
  //   1. Pre-permission, the observer emits a single anonymized entry on
  //      Firefox/Safari (empty deviceId/label). A persisted id like 'default'
  //      won't be in that list, but it's still the correct value to send to
  //      `getUserMedia` — the browser resolves it. Returning '' would make
  //      the dropdown show "Select Option" forever.
  //   2. `MediaDeviceMenu` already runs its own fallback (setDefaultDevice)
  //      when its enumeration doesn't contain the incoming id, swapping in
  //      the first available device and notifying us via the change handler.
  //      Validating again here would clobber that real id on the next render.
  // Only resolve to '' when our enumeration has definitively returned an
  // empty list (no devices of that kind on the machine).
  const videoDeviceIdWithNone = useMemo(() => {
    if (videoDevices && videoDevices.length === 0) return ''
    return videoDeviceId
  }, [videoDeviceId, videoDevices])

  const audioDeviceIdWithNone = useMemo(() => {
    if (audioDevices && audioDevices.length === 0) return ''
    return audioDeviceId
  }, [audioDeviceId, audioDevices])

  const [error, setError] = useState<Error | null>(null)

  const onError = useCallback((err: Error) => {
    setError(err)
  }, [])

  // Clear stale error state whenever we kick off a new capture attempt
  // (device change, or first time a device id resolves).
  useEffect(() => {
    setError(null)
  }, [audioDeviceIdWithNone, videoDeviceIdWithNone])

  // One combined usePreviewTracks call -> one getUserMedia({audio, video})
  // -> one browser permission prompt. Matches the LiveKit PreJoin prefab.
  //
  // We pass `{ deviceId: audioDeviceId }` (the raw persisted choice, which
  // may be the empty string) instead of `audioDeviceIdWithNone`. The
  // "withNone" id is only resolvable AFTER permission has been granted
  // (pre-permission, `enumerateDevices` returns anonymous ids that won't
  // match a persisted choice). Gating on it would leave both kinds as
  // `false` and no prompt would ever fire. An empty deviceId is fine —
  // `getUserMedia` interprets it as the default device.
  //
  // We DO drop a kind to `false` once enumeration has resolved to an
  // empty list — that means there genuinely are no devices of that kind
  // and we mustn't request them (otherwise the combined call would fail
  // and we'd lose the other kind too).
  const tracks = usePreviewTracks(
    {
      audio:
        audioDevices && audioDevices.length === 0
          ? false
          : { deviceId: audioDeviceId },
      video:
        videoDevices && videoDevices.length === 0
          ? false
          : { deviceId: videoDeviceId },
    },
    onError
  )

  const videoTrack = useMemo(() => {
    return tracks?.filter(
      (track) => track.kind === 'video'
    )[0] as LocalVideoTrack
  }, [tracks])

  const audioTrack = useMemo(() => {
    return tracks?.filter(
      (track) => track.kind === 'audio'
    )[0] as LocalAudioTrack
  }, [tracks])

  const videoEl = useRef(null)
  useEffect(() => {
    if (videoEl.current && videoTrack) {
      videoTrack?.detach()

      videoTrack.unmute()
      videoTrack.attach(videoEl.current)
    }

    return () => {
      videoTrack?.detach()
      videoTrack?.stop()
    }
  }, [videoTrack])

  useEffect(() => {
    return () => {
      audioTrack?.stop()
    }
  }, [audioTrack])

  // The single combined getUserMedia call is all-or-nothing: if it
  // resolves we have both kinds granted, if it rejects we treat both as
  // failed. Per-kind states are mirrors of the combined state so existing
  // consumers (DeviceConfigurationStage, AvCheck) keep working without
  // having to branch on which kind broke.
  const permissionState: DetailedPermissionState = useMemo(() => {
    if (audioTrack || videoTrack) return 'granted'
    if (error) return mapErrorToDetailedState(error)
    return 'initializing'
  }, [audioTrack, videoTrack, error])

  return {
    audioDevices,
    videoDevices,
    videoTrack,
    audioTrack,
    videoEl,
    audioDeviceId: audioDeviceIdWithNone,
    videoDeviceId: videoDeviceIdWithNone,
    onActiveVideoDeviceChange,
    onActiveAudioDeviceChange,
    audioPermissionState: permissionState,
    videoPermissionState: permissionState,
    permissionState,
  }
}

export type MediaDeviceListenerValue = ReturnType<typeof useMediaDeviceListener>
